import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilState } from 'recoil';
import { useState, useEffect, useRef } from 'react';
import QuickFind from '@Components/Header/QuickFind';

const AnimatedQuickFind = (props) => {
    const [keywordSearchState, setKeywordSearchState] = useRecoilState(atoms.keywordSearchStateAtom);
    const [animatedTerm, setAnimatedTerm] = useState('');
    const [isAnimating, setIsAnimating] = useState(true);
    const [showCursor, setShowCursor] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const timeoutRef = useRef(null);
    const cursorBlinkRef = useRef(null);
 
    let currTermIndex = 0;
    let currCharIndex = 0;
    let typingSpeed = 50;
    let pauseDuration = 3000;
    let cursorBlinkSpeed = 500; 

    const cursorStyle = {
        opacity: showCursor ? 1 : 0
    };


    const getAnimatedPlaceholder = () => {
        if (!isFocused && keywordSearchState.SearchText) return '';
        if (isFocused) return '';
        if (!isAnimating) {
            if (props.defaultText) return props.defaultText;
            return keywordSearchState.QuickFindPlaceholder;
        }

        return (
            <>
                {animatedTerm}
                <span style={cursorStyle} className="animated-cursor"></span>
            </>
        );
    };

    const getDefaultPlaceholder = () => {
        if (isFocused || keywordSearchState.SearchText) return '';        
        return keywordSearchState.QuickFindPlaceholder;
    };


    const startCursorBlink = () => {
        if (cursorBlinkRef.current) {
            clearInterval(cursorBlinkRef.current);
        }

        cursorBlinkRef.current = setInterval(() => {
            setShowCursor(prev => !prev);
        }, cursorBlinkSpeed);
    };


    const stopCursorBlink = () => {
        if (cursorBlinkRef.current) {
            clearInterval(cursorBlinkRef.current);
            cursorBlinkRef.current = null;
        }
    };

    const handleFocus = (e) => {
        setIsAnimating(false);
        stopCursorBlink();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsFocused(true);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
    };

    const typeNextChar = (skipFirst) => {
        if (!isAnimating) return;

        if (currCharIndex < keywordSearchState.AutocompleteTerms[currTermIndex].length) {
            if (!skipFirst) {
                setAnimatedTerm((prev) => prev + keywordSearchState.AutocompleteTerms[currTermIndex][currCharIndex]);
                setShowCursor(true); // Show cursor when typing
            }
            timeoutRef.current = setTimeout(() => typeNextChar(false), typingSpeed);
            currCharIndex = currCharIndex + 1;
        } else {
            timeoutRef.current = setTimeout(deleteText, pauseDuration);
        }
    }

    const deleteText = () => {
        if (!isAnimating) return;

        if (currCharIndex > 0 && currTermIndex + 1 < keywordSearchState.AutocompleteTerms.length) {
            setAnimatedTerm('');
            currCharIndex = 0;
            deleteText();
        }
        else if (currTermIndex + 1 < keywordSearchState.AutocompleteTerms.length) {
            currTermIndex = (currTermIndex + 1) % keywordSearchState.AutocompleteTerms.length;
            timeoutRef.current = setTimeout(() => typeNextChar(false), (typingSpeed * 10));
        }
        else {
            // If we've gone through all terms, start over from the first term
            currTermIndex = 0;
            currCharIndex = 0;
            setAnimatedTerm('');
            timeoutRef.current = setTimeout(() => typeNextChar(false), (typingSpeed * 10));
        }
    }

    const checkScrollPosition = () => {
        const scrollPosition = window.scrollY;
        const isAtTop = scrollPosition < 50;

        if (!isAtTop && isAnimating) {
            setIsAnimating(false);
            stopCursorBlink();

            setAnimatedTerm(keywordSearchState.QuickFindPlaceholder);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    };

    const onTextChange = (e) => {
        if (!e || !e.target || e.key === 'Enter') return;

        setKeywordSearchState(prevState => ({
            ...prevState,
            SearchText: e.target.value
        }));
    }

    useEffect(() => {
        if (keywordSearchState.AutocompleteTerms &&
            keywordSearchState.AutocompleteTerms.length > 0 &&
            isAnimating) {

            // Clear any existing timeouts
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Reset animation state
            currTermIndex = 0;
            currCharIndex = 0;

            // Start cursor blinking
            startCursorBlink();

            // Start new animation sequence
            setAnimatedTerm(keywordSearchState.AutocompleteTerms[currTermIndex][currCharIndex]);
            timeoutRef.current = setTimeout(() => typeNextChar(true), typingSpeed);
        }

        window.addEventListener('scroll', checkScrollPosition);
        return () => {
            window.removeEventListener('scroll', checkScrollPosition);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            stopCursorBlink();
        };
    }, [keywordSearchState.AutocompleteTerms, isAnimating]);

    return (
        <div
            className="animated-quick-find-wrapper"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={onTextChange}
        >
            <QuickFind
                placeholder={keywordSearchState.AutocompleteTerms && keywordSearchState.AutocompleteTerms.length > 0
                    ? getAnimatedPlaceholder()
                    : getDefaultPlaceholder()}
                actionurl={keywordSearchState.QuickFindActionURL}
                searchTypes={keywordSearchState.QuickFindSearchTypes}
                partsUrl={keywordSearchState.PartsSearchURL}
                attachmentsUrl={keywordSearchState.AttachmentsSearchURL}
                value={keywordSearchState.SearchText}
                onTextChange={onTextChange}
                hideLabel={true}
                id={'animated-quick-find'}
                container={props.container}
                isStickyHeader={props.isStickyHeader}
            />
        </div>
    );
};

export default AnimatedQuickFind;